import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { getHeroList, getContentEnemies, saveGuide, getGuideDetail } from '../../api/senaApi';
import { heroPortrait, skillImg } from './senaAssets';
import './SenaGuideWrite.css';


const CONTENT_LABELS = {
  siege: '공성전', guild_war: '길드전', total_war: '총력전',
  expedition: '강림원정대', raid: '레이드(일반)', raid_sp: '레이드(돌발)',
};
// 아군 스킬 팔레트 줄 순서 (위 → 아래). 맨 윗줄은 각성 스킬(3) 전용 자리
const PALETTE_SKILL_ROWS = [3, 2, 1];

const FIXED_ROUND_MAP = { siege: 3, expedition: 2, raid: 1, raid_sp: 1 };
const MAX_TURN_MAP = { siege: 70, expedition: 70, raid: 100, raid_sp: 100 };

const FORMATIONS = [
  { id: 'basic',    label: '기본진형',   front: [1, 2],       back: [3, 4, 5] },
  { id: 'balance',  label: '밸런스진형', front: [1, 2, 3],    back: [4, 5] },
  { id: 'defense',  label: '방어진형',   front: [1],          back: [2, 3, 4, 5] },
  { id: 'protect',  label: '보호진형',   front: [1, 2, 3, 4], back: [5] },
];

const SET_OPTIONS = [
  { v: '0', l: '상관없음' }, { v: '1', l: '복수자' }, { v: '2', l: '암살자' },
  { v: '3', l: '성기사' }, { v: '4', l: '수문장' }, { v: '5', l: '추적자' },
  { v: '6', l: '주술사' }, { v: '7', l: '조율자' }, { v: '8', l: '선봉장' },
  { v: '9', l: '수호자' },
];

const SPEED_OPTIONS = [
  { v: '0', l: '상관없음' }, { v: '1', l: '1순위' }, { v: '2', l: '2순위' },
  { v: '3', l: '3순위' }, { v: '4', l: '4순위' }, { v: '5', l: '5순위' },
  { v: '6', l: '직접입력' },
];

const defaultSetting = () => ({ setOption: '0', critRate: 0, weakRate: 0, speedOption: '0', speedCustom: '', note: '' });
const emptyAllies = () => [null, null, null, null, null];
const emptySettings = () => Array.from({ length: 5 }, defaultSetting);

export default function SenaGuideWrite() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const location = useLocation();
  const passedGuideData = location.state?.guideData;
  const contentType = params.get('type') || 'siege';
  const subType = params.get('sub') || '';
  const editId = params.get('id');
  const copyFrom = params.get('copyFrom'); // 복사하기: 원본 내용 prefill + 저장 시 새 글로 INSERT
  const userId = localStorage.getItem('userId') || '게스트';

  // ===== 기본 상태 =====
  const [title, setTitle] = useState('');
  const fixedRounds = FIXED_ROUND_MAP[contentType];
  const [totalRounds, setTotalRounds] = useState(fixedRounds || 1);
  useEffect(() => { if (fixedRounds) setTotalRounds(fixedRounds); }, [fixedRounds]);
  const [includeEnemy, setIncludeEnemy] = useState(false);
  const maxTurn = MAX_TURN_MAP[contentType] || 70;
  const [description, setDescription] = useState('');

  // ===== 영웅 데이터 =====
  const [allyHeroes, setAllyHeroes] = useState([]);
  const [enemyHeroes, setEnemyHeroes] = useState([]);
  const [contentEnemies, setContentEnemies] = useState([]);

  // ===== 라운드별 아군 팀 + 세팅 + 진형 =====
  // key = 1,2,3 (1-indexed round number)
  const [formationByRound, setFormationByRound] = useState({ 1: 'basic', 2: null, 3: null });
  const [alliesByRound, setAlliesByRound] = useState({ 1: emptyAllies(), 2: emptyAllies(), 3: emptyAllies() });
  const [settingsByRound, setSettingsByRound] = useState({ 1: emptySettings(), 2: emptySettings(), 3: emptySettings() });

  // ===== 라운드/스킬 =====
  const [rounds, setRounds] = useState(Array.from({ length: fixedRounds || 1 }, () => []));
  const [activeRound, setActiveRound] = useState(0); // 0-indexed
  const [dragIdx, setDragIdx] = useState(null);
  const [settingDragIdx, setSettingDragIdx] = useState(null);
  const [heroSearch, setHeroSearch] = useState('');
  const [saving, setSaving] = useState(false);

  // ===== 현재 라운드 파생값 =====
  const currentRound = activeRound + 1; // 1-indexed
  const selectedAllies = alliesByRound[currentRound] || emptyAllies();
  const heroSettings = settingsByRound[currentRound] || emptySettings();
  // R2/R3가 null이면 R1 진형 폴백 (표시용)
  const formation = formationByRound[currentRound] || formationByRound[1] || 'basic';

  // ===== 현재 라운드 전용 setter =====
  const setSelectedAlliesForCurrent = (updaterOrValue) => {
    setAlliesByRound(prev => {
      const base = prev[currentRound] || emptyAllies();
      const next = typeof updaterOrValue === 'function' ? updaterOrValue(base) : updaterOrValue;
      return { ...prev, [currentRound]: next };
    });
  };
  const setHeroSettingsForCurrent = (updaterOrValue) => {
    setSettingsByRound(prev => {
      const base = prev[currentRound] || emptySettings();
      const next = typeof updaterOrValue === 'function' ? updaterOrValue(base) : updaterOrValue;
      return { ...prev, [currentRound]: next };
    });
  };
  const setFormationForCurrent = (v) => {
    setFormationByRound(prev => ({ ...prev, [currentRound]: v }));
  };

  // ===== 가이드 데이터 적용 =====
  const applyGuideData = (g, heroes) => {
    if (!g) return;
    setTitle(g.title || '');
    setDescription(g.description || '');
    setIncludeEnemy(g.includeEnemySkill === 'Y');

    // 라운드별 진형
    const fms = {
      1: g.formation || 'basic',
      2: g.formationR2 || null,
      3: g.formationR3 || null,
    };
    setFormationByRound(fms);

    // 라운드별 아군 팀
    const newAllies = { 1: emptyAllies(), 2: emptyAllies(), 3: emptyAllies() };
    const newSettings = { 1: emptySettings(), 2: emptySettings(), 3: emptySettings() };
    (g.allies || []).forEach(a => {
      const r = a.roundNo || 1;
      if (r < 1 || r > 3) return;
      const idx = (a.slotNo || 1) - 1;
      if (idx < 0 || idx > 4) return;
      const hero = heroes.find(h => h.heroId === a.heroId);
      if (!hero) return;
      newAllies[r][idx] = hero;
      newSettings[r][idx] = {
        setOption: a.setOption || '0',
        critRate: a.critRate || 0,
        weakRate: a.weakRate || 0,
        speedOption: a.speedOption || '0',
        speedCustom: a.speedCustom || '',
        note: a.note || '',
      };
    });
    setAlliesByRound(newAllies);
    setSettingsByRound(newSettings);

    // 스킬
    const maxRound = g.totalRounds || 1;
    const rArr = Array.from({ length: maxRound }, () => []);
    (g.skills || []).forEach(sk => {
      if (sk.roundNo >= 1 && sk.roundNo <= maxRound) {
        rArr[sk.roundNo - 1].push({ ...sk, heroNm: sk.heroNm, isEnemy: sk.isEnemy });
      }
    });
    rArr.forEach(r => r.sort((a, b) => a.seqNo - b.seqNo));
    setRounds(rArr);
  };

  // ===== 데이터 로드 =====
  useEffect(() => {
    getHeroList('ALLY').then(r => {
      const heroes = r.data || [];
      setAllyHeroes(heroes);
      const sourceId = editId || copyFrom; // 수정/복사 모두 원본 데이터를 prefill
      if (sourceId) {
        if (passedGuideData) {
          applyGuideData(passedGuideData, heroes);
        } else {
          getGuideDetail(Number(sourceId)).then(res => applyGuideData(res.data, heroes)).catch(() => {});
        }
      }
    }).catch(() => {});

    getHeroList('ENEMY').then(r => setEnemyHeroes(r.data || [])).catch(() => {});
    if (contentType && subType) {
      getContentEnemies(contentType, subType).then(r => setContentEnemies(r.data || [])).catch(() => {});
    }
  }, [contentType, subType, editId, copyFrom]);

  useEffect(() => {
    setRounds(prev => {
      const arr = [...prev];
      while (arr.length < totalRounds) arr.push([]);
      return arr.slice(0, totalRounds);
    });
    if (activeRound >= totalRounds) setActiveRound(totalRounds - 1);
  }, [totalRounds, activeRound]);

  // ===== 영웅 선택 (현재 라운드 기준) =====
  const isJjol = (hero) => hero?.heroNm === '쫄';

  const handleSelectAlly = (hero) => {
    const emptyIdx = selectedAllies.findIndex(h => h === null);
    if (emptyIdx < 0) { alert('슬롯이 모두 차있습니다.'); return; }
    setSelectedAlliesForCurrent(prev => {
      const next = [...prev];
      if (!isJjol(hero)) {
        const existIdx = next.findIndex(h => h && h.heroId === hero.heroId);
        if (existIdx >= 0) {
          next[existIdx] = null;
          setHeroSettingsForCurrent(s => { const ns = [...s]; ns[existIdx] = defaultSetting(); return ns; });
        }
      }
      next[emptyIdx] = hero;
      return next;
    });
    setHeroSearch('');
  };

  const handleRemoveAlly = (idx) => {
    setSelectedAlliesForCurrent(prev => { const next = [...prev]; next[idx] = null; return next; });
    setHeroSettingsForCurrent(prev => { const next = [...prev]; next[idx] = defaultSetting(); return next; });
  };

  const updateSetting = (idx, field, value) => {
    setHeroSettingsForCurrent(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  // ===== 드래그앤드롭 슬롯 스왑 =====
  const handleSettingDragStart = (idx) => setSettingDragIdx(idx);
  const handleSettingDrop = (dropIdx) => {
    if (settingDragIdx === null || settingDragIdx === dropIdx) return;
    setSelectedAlliesForCurrent(prev => {
      const next = [...prev];
      [next[settingDragIdx], next[dropIdx]] = [next[dropIdx], next[settingDragIdx]];
      return next;
    });
    setHeroSettingsForCurrent(prev => {
      const next = [...prev];
      [next[settingDragIdx], next[dropIdx]] = [next[dropIdx], next[settingDragIdx]];
      return next;
    });
    setSettingDragIdx(null);
  };

  // ===== R1 팀을 현재 라운드로 복제 =====
  const copyFromRound1 = () => {
    if (currentRound === 1) return;
    if (!window.confirm(`라운드 1의 진형/영웅/세팅을 라운드 ${currentRound}에 복제하시겠습니까?`)) return;
    setAlliesByRound(prev => ({ ...prev, [currentRound]: [...(prev[1] || emptyAllies())] }));
    setSettingsByRound(prev => ({
      ...prev,
      [currentRound]: (prev[1] || emptySettings()).map(s => ({ ...s })),
    }));
    setFormationByRound(prev => ({ ...prev, [currentRound]: prev[1] || 'basic' }));
  };

  // ===== 턴 계산 =====
  const turnMultiplier = includeEnemy ? 2 : 4;
  const getStartTurn = (roundIdx) => {
    if (roundIdx === 0) return 0;
    const prevRound = rounds[roundIdx - 1];
    const prevStart = getStartTurn(roundIdx - 1);
    if (prevRound.length === 0) return prevStart;
    return prevStart + (prevRound.length - 1) * turnMultiplier;
  };
  const getTurn = (roundIdx, seqIdx) => getStartTurn(roundIdx) + seqIdx * turnMultiplier;
  const canAddSkill = (roundIdx) => getTurn(roundIdx, rounds[roundIdx].length) < maxTurn;

  // ===== 스킬 배치 =====
  const handleAddSkill = (skill, isEnemy) => {
    setRounds(prev => {
      const next = prev.map(r => [...r]);
      if (!canAddSkill(activeRound)) { alert(`${maxTurn}턴을 초과하여 스킬을 등록할 수 없습니다.`); return prev; }
      next[activeRound].push({ ...skill, isEnemy: isEnemy ? 'Y' : 'N' });
      return next;
    });
  };
  const handleRemoveSkill = (roundIdx, seqIdx) => {
    setRounds(prev => { const next = prev.map(r => [...r]); next[roundIdx].splice(seqIdx, 1); return next; });
  };
  const handleSkillDragStart = (seqIdx) => setDragIdx(seqIdx);
  const handleSkillDrop = (roundIdx, dropIdx) => {
    if (dragIdx === null || dragIdx === dropIdx) return;
    setRounds(prev => { const next = prev.map(r => [...r]); const [moved] = next[roundIdx].splice(dragIdx, 1); next[roundIdx].splice(dropIdx, 0, moved); return next; });
    setDragIdx(null);
  };

  // ===== 저장 =====
  const handleSave = async () => {
    if (!title.trim()) { alert('제목을 입력해주세요.'); return; }

    // 라운드별 아군을 flatten
    const allyList = [];
    for (let r = 1; r <= totalRounds; r++) {
      const allies = alliesByRound[r] || emptyAllies();
      const settings = settingsByRound[r] || emptySettings();
      allies.forEach((h, i) => {
        if (!h) return;
        allyList.push({
          heroId: h.heroId,
          slotNo: i + 1,
          roundNo: r,
          setOption: settings[i].setOption,
          critRate: settings[i].critRate,
          weakRate: settings[i].weakRate,
          speedOption: settings[i].speedOption,
          speedCustom: settings[i].speedCustom,
          note: settings[i].note,
        });
      });
    }
    if (allyList.length === 0) { alert('아군을 최소 1명 선택해주세요.'); return; }

    const dto = {
      guideId: editId ? Number(editId) : undefined,
      contentType, subType, title, authorNm: userId,
      description: description || null,
      totalRounds, maxTurn,
      includeEnemySkill: includeEnemy ? 'Y' : 'N',
      formation: formationByRound[1] || 'basic',
      formationR2: totalRounds >= 2 ? (formationByRound[2] || null) : null,
      formationR3: totalRounds >= 3 ? (formationByRound[3] || null) : null,
      allies: allyList,
      speeds: [],
      skills: rounds.flatMap((round, ri) =>
        round.map((sk, si) => ({ roundNo: ri + 1, seqNo: si, skillId: sk.skillId, isEnemy: sk.isEnemy }))
      ),
    };

    setSaving(true);
    try {
      await saveGuide(dto);
      alert('공략이 저장되었습니다.');
      navigate(-1);
    } catch (e) { alert(e?.response?.data || '저장에 실패했습니다.'); }
    finally { setSaving(false); }
  };

  const activeStartTurn = getStartTurn(activeRound);
  const activeLastTurn = rounds[activeRound]?.length > 0 ? getTurn(activeRound, rounds[activeRound].length - 1) : activeStartTurn;
  const fm = FORMATIONS.find(f => f.id === formation);

  return (
    <div className="sena-page">
      <header className="sena-header">
        <button className="siege-back-btn" onClick={() => navigate(-1)}>← 뒤로</button>
        <button className="siege-home-btn" onClick={() => navigate('/sena')}>홈</button>
        <h1 className="sena-header-guild">{CONTENT_LABELS[contentType] || '공략'} 작성</h1>
        <p className="sena-header-sub">{subType && `(${subType})`}</p>
      </header>

      <div className="gw-container">
        {/* 1. 기본 정보 */}
        <section className="gw-section">
          <h3 className="gw-section-title">기본 정보</h3>
          <div className="gw-form-row">
            <label className="gw-label">제목</label>
            <input className="gw-input" value={title} onChange={e => setTitle(e.target.value)} placeholder="공략 제목" />
          </div>
          <div className="gw-form-row">
            <label className="gw-label">작성자</label>
            <input className="gw-input gw-input-sm" value={localStorage.getItem('userName') || userId} disabled />
          </div>
          <div className="gw-form-row">
            <label className="gw-label">적군 정보</label>
            <div className="gw-enemy-tags">
              {contentEnemies.length > 0 ? contentEnemies.map(ce => {
                const hero = enemyHeroes.find(h => h.heroId === ce.heroId);
                return <span key={ce.contentEnemyId} className="gw-tag enemy">{hero?.heroNm || `적군${ce.orderNo}`}</span>;
              }) : <span className="gw-tag empty">자동 기입 (컨텐츠별 적군 등록 필요)</span>}
            </div>
          </div>
        </section>

        {/* 2. 설정 */}
        <section className="gw-section">
          <h3 className="gw-section-title">설정</h3>
          <div className="gw-settings-row">
            <div className="gw-form-row">
              <label className="gw-label">총 라운드</label>
              {fixedRounds ? (
                <input className="gw-input gw-input-sm" value={`${fixedRounds} 라운드 (고정)`} disabled />
              ) : (
                <select className="gw-select" value={totalRounds} onChange={e => setTotalRounds(Number(e.target.value))}>
                  <option value={1}>1 라운드</option>
                  <option value={2}>2 라운드</option>
                  <option value={3}>3 라운드</option>
                </select>
              )}
            </div>
            <div className="gw-form-row">
              <label className="gw-label">최대 턴</label>
              <input className="gw-input gw-input-sm" value={maxTurn} disabled />
            </div>
            <div className="gw-form-row">
              <label className="gw-label">적 스킬 포함</label>
              <div className="gw-toggle-group">
                <button className={`gw-toggle-btn ${includeEnemy ? 'active' : ''}`} onClick={() => setIncludeEnemy(true)}>예</button>
                <button className={`gw-toggle-btn ${!includeEnemy ? 'active' : ''}`} onClick={() => setIncludeEnemy(false)}>아니오</button>
              </div>
            </div>
          </div>
        </section>

        {/* 3. 진형 및 세팅 */}
        <section className="gw-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
            <h3 className="gw-section-title" style={{ margin: 0 }}>진형 및 세팅</h3>

            {/* 라운드 탭 (totalRounds > 1 일 때만) */}
            {totalRounds > 1 && (
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                {Array.from({ length: totalRounds }, (_, i) => (
                  <button
                    key={i}
                    className={`gw-round-tab ${activeRound === i ? 'active' : ''}`}
                    onClick={() => setActiveRound(i)}
                  >
                    라운드 {i + 1}
                  </button>
                ))}
                {currentRound > 1 && (
                  <button
                    type="button"
                    onClick={copyFromRound1}
                    style={{
                      padding: '6px 12px',
                      background: '#fff',
                      border: '2px solid #f0d6c0',
                      borderRadius: 8,
                      color: '#c88050',
                      fontFamily: 'Jua, sans-serif',
                      fontSize: 12,
                      cursor: 'pointer',
                    }}
                  >
                    📋 R1 팀 복제
                  </button>
                )}
              </div>
            )}
          </div>

          {totalRounds > 1 && (
            <p style={{ fontSize: 12, color: '#8a6a50', margin: '0 0 12px' }}>
              현재 편집 중: <strong>라운드 {currentRound}</strong> — 진형/영웅/세팅은 라운드별로 독립 저장됩니다.
            </p>
          )}

          {/* 진형 선택 */}
          <div className="gw-formation-group" style={{ marginBottom: 14 }}>
            {FORMATIONS.map(f => (
              <button key={f.id} className={`gw-formation-btn ${formation === f.id ? 'active' : ''}`}
                onClick={() => setFormationForCurrent(f.id)}>{f.label}</button>
            ))}
          </div>

          {/* 영웅 검색 */}
          <div className="gw-hero-search-wrap" style={{ marginBottom: 14 }}>
            <input className="gw-hero-search" type="text" placeholder="영웅 이름 검색..."
              value={heroSearch} onChange={e => setHeroSearch(e.target.value)} />
            {heroSearch.length > 0 && (
              <div className="gw-hero-dropdown">
                {allyHeroes
                  .filter(h => h.heroNm.includes(heroSearch))
                  .filter(h => isJjol(h) || !selectedAllies.some(s => s && s.heroId === h.heroId))
                  .map(hero => (
                    <button key={hero.heroId} className="gw-hero-dropdown-item" onClick={() => handleSelectAlly(hero)}>
                      {hero.heroNm}
                    </button>
                  ))}
                {allyHeroes.filter(h => h.heroNm.includes(heroSearch)).filter(h => isJjol(h) || !selectedAllies.some(s => s && s.heroId === h.heroId)).length === 0 && (
                  <div className="gw-hero-dropdown-empty">검색 결과 없음</div>
                )}
              </div>
            )}
          </div>

          {/* 5개 슬롯 row */}
          <div className="gw-setting-rows">
            {selectedAllies.map((hero, idx) => {
              const slotNum = idx + 1;
              const isFront = fm?.front.includes(slotNum);
              const s = heroSettings[idx];
              const portrait = hero ? heroPortrait(hero.heroNm, 'ALLY') : null;

              return (
                <div key={idx} className={`gw-setting-row ${hero ? '' : 'empty'}`}
                  draggable={!!hero}
                  onDragStart={() => hero && handleSettingDragStart(idx)}
                  onDragOver={e => e.preventDefault()}
                  onDrop={() => handleSettingDrop(idx)}
                >
                  <div className="gw-sr-slot">
                    <span className={`gw-slot-badge ${isFront ? 'front' : 'back'}`}>
                      {isFront ? '전열' : '후열'}
                    </span>
                    <span className="gw-sr-slot-num">{slotNum}</span>
                  </div>

                  <div className="gw-sr-hero">
                    {hero ? (
                      <>
                        {portrait ? <img className="gw-sr-portrait" src={portrait} alt={hero.heroNm} />
                          : <span className="gw-sr-hero-name">{hero.heroNm}</span>}
                        <button className="gw-sr-remove" onClick={() => handleRemoveAlly(idx)}>×</button>
                      </>
                    ) : (
                      <span className="gw-sr-empty">빈 슬롯</span>
                    )}
                  </div>

                  {hero && (
                    <>
                      <div className="gw-sr-field">
                        <label className="gw-sr-label">세트</label>
                        <select className="gw-sr-select" value={s.setOption} onChange={e => updateSetting(idx, 'setOption', e.target.value)}>
                          {SET_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                        </select>
                      </div>
                      <div className="gw-sr-field">
                        <label className="gw-sr-label">치확</label>
                        <input className="gw-sr-input" type="number" min={0} max={100}
                          value={s.critRate} onChange={e => updateSetting(idx, 'critRate', Math.min(100, Number(e.target.value) || 0))} />
                      </div>
                      <div className="gw-sr-field">
                        <label className="gw-sr-label">약확</label>
                        <input className="gw-sr-input" type="number" min={0} max={100}
                          value={s.weakRate} onChange={e => updateSetting(idx, 'weakRate', Math.min(100, Number(e.target.value) || 0))} />
                      </div>
                      <div className="gw-sr-field">
                        <label className="gw-sr-label">속공</label>
                        <select className="gw-sr-select" value={s.speedOption} onChange={e => updateSetting(idx, 'speedOption', e.target.value)}>
                          {SPEED_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                        </select>
                        {s.speedOption === '6' && (
                          <input className="gw-sr-input gw-sr-speed-custom" placeholder="직접입력"
                            value={s.speedCustom} onChange={e => updateSetting(idx, 'speedCustom', e.target.value)} />
                        )}
                      </div>
                      <div className="gw-sr-field">
                        <label className="gw-sr-label">기타</label>
                        <input className="gw-sr-input gw-sr-note" placeholder="메모"
                          value={s.note} onChange={e => updateSetting(idx, 'note', e.target.value)} />
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
          <p className="gw-hint">드래그하여 슬롯 데이터를 스왑할 수 있습니다.</p>
        </section>

        {/* 4. 스킬 배치 */}
        <section className="gw-section">
          <h3 className="gw-section-title">스킬 배치</h3>

          {selectedAllies.filter(Boolean).length > 0 && (
            <div className="gw-palette">
              <p className="gw-palette-label">
                아군 스킬 {totalRounds > 1 && <span style={{ fontSize: 11, color: '#8a6a50' }}>— 라운드 {currentRound} 팀 기준</span>}
              </p>
              <div className="gw-palette-grid">
                {selectedAllies.filter(Boolean).map(hero => (
                  <div key={hero.heroId} className="gw-palette-hero">
                    <span className="gw-palette-hero-name">{hero.heroNm}</span>
                    <div className="gw-palette-skills-col">
                      {/* 게임 화면과 같은 순서(위=각성/스킬2, 아래=스킬1)로 줄을 고정한다.
                          미각성 영웅은 맨 윗줄(각성 자리)을 비워 영웅끼리 줄이 맞는다 */}
                      {PALETTE_SKILL_ROWS.map(no => {
                        const sk = (hero.skills || []).find(s => s.skillNo === no);
                        if (!sk) return <div key={no} className="gw-skill-btn-empty" />;
                        const img = skillImg(hero.heroNm, 'ALLY', no);
                        return (
                          <button key={sk.skillId} className="gw-skill-btn-img ally"
                            onClick={() => handleAddSkill({ ...sk, heroNm: hero.heroNm }, false)}>
                            {img ? <img src={img} alt={sk.skillNm} className="gw-skill-btn-icon" />
                              : <span>{sk.skillNm || `스킬${sk.skillNo}`}</span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {includeEnemy && contentEnemies.length > 0 && (
            <div className="gw-palette">
              <p className="gw-palette-label">적군 스킬</p>
              <div className="gw-palette-grid">
                {contentEnemies.map(ce => {
                  const hero = enemyHeroes.find(h => h.heroId === ce.heroId);
                  if (!hero) return null;
                  return (
                    <div key={ce.contentEnemyId} className="gw-palette-hero">
                      <span className="gw-palette-hero-name enemy">{hero.heroNm}</span>
                      <div className="gw-palette-skills">
                        {(hero.skills || []).map(sk => (
                          <button key={sk.skillId} className="gw-skill-btn enemy"
                            onClick={() => handleAddSkill({ ...sk, heroNm: hero.heroNm }, true)}>
                            [적] {sk.skillNm || `스킬${sk.skillNo}`}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="gw-rounds">
            <div className="gw-round-tabs">
              {Array.from({ length: totalRounds }, (_, i) => (
                <button key={i} className={`gw-round-tab ${activeRound === i ? 'active' : ''}`}
                  onClick={() => setActiveRound(i)}>라운드 {i + 1}</button>
              ))}
            </div>

            <div className="gw-round-body">
              <div className="gw-round-info">
                <span>라운드 {activeRound + 1}</span>
                <span className="gw-round-count">{rounds[activeRound]?.length || 0}개 스킬</span>
                <span className="gw-round-hint">시작턴: {activeStartTurn} | 마지막턴: {activeLastTurn} | 간격: {turnMultiplier}</span>
              </div>

              <div className="gw-skill-timeline">
                {(rounds[activeRound] || []).map((sk, idx) => {
                  const turn = getTurn(activeRound, idx);
                  const heroType = sk.isEnemy === 'Y' ? 'ENEMY' : 'ALLY';
                  const img = skillImg(sk.heroNm, heroType, sk.skillNo);
                  return (
                    <div key={idx} className={`gw-skill-slot ${sk.isEnemy === 'Y' ? 'enemy' : 'ally'}`}
                      draggable onDragStart={() => handleSkillDragStart(idx)}
                      onDragOver={e => e.preventDefault()} onDrop={() => handleSkillDrop(activeRound, idx)}>
                      <span className="gw-skill-turn">{turn}턴</span>
                      {img ? <img src={img} alt={sk.skillNm} className="gw-skill-slot-img" /> : (
                        <><span className="gw-skill-hero">{sk.isEnemy === 'Y' ? `[적]${sk.heroNm}` : sk.heroNm}</span>
                        <span className="gw-skill-name">{sk.skillNm || `스킬${sk.skillNo}`}</span></>
                      )}
                      <button className="gw-skill-remove" onClick={() => handleRemoveSkill(activeRound, idx)}>×</button>
                    </div>
                  );
                })}
                {(rounds[activeRound]?.length || 0) === 0 && (
                  <p className="gw-round-empty">위 스킬을 클릭하여 배치하세요.</p>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* 5. 설명 */}
        <section className="gw-section">
          <h3 className="gw-section-title">설명 (선택)</h3>
          <textarea className="gw-textarea" value={description} onChange={e => setDescription(e.target.value)}
            placeholder="공략에 대한 추가 설명, 팁, 주의사항 등을 자유롭게 작성하세요." rows={5} />
        </section>

        {/* 저장 */}
        <div className="gw-footer">
          <button className="gw-btn" onClick={() => navigate(-1)}>취소</button>
          <button className="gw-btn primary" onClick={handleSave} disabled={saving}>
            {saving ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
}
